import { IPriceConfigTab } from "@/store/pricesTabStore";
import { updateDisplayNamePriceConfigInStoreAndAPI } from "@/store/pricesTabStoreACTIONS";
import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import clsx from "clsx";

// Shared by the label and the input so the text does not shift on double-click.
const EDITABLE_TEXT_BOX_CLASS =
  "box-border h-[1.75em] w-full rounded border px-1 normal-case";

export interface EditableTextProps {
  initialText: string;
  config: IPriceConfigTab;
  updateStoreLocation: string;
  getAccessTokenSilently: Function;
  settingsConfigsFromStore?: IPriceConfigTab[];
}

const EditableText: React.FC<EditableTextProps> = ({
  initialText,
  config,
  updateStoreLocation,
  getAccessTokenSilently,
  settingsConfigsFromStore,
}) => {
  const [text, setText] = useState<string>(initialText);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(initialText);

  useEffect(() => {
    setText(initialText);
    setInputValue(initialText); // If you also want inputValue to sync
  }, [initialText]);

  // locations to update stores.
  const handleDoubleClick = () => {
    if (config.displayName == "Default") {
      toast.error("Unable to change Default");
      return;
    }
    setIsEditing(true);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const newName = e.target.value; // settingsStore values will always be here as they are required to produce the data to create the component.
    const arrOfCurrentNames = settingsConfigsFromStore!.map(
      (config) => config.displayName,
    );

    if (arrOfCurrentNames.includes(newName) && newName != initialText) {
      setText(initialText);
      setInputValue(initialText);
      setIsEditing(false);
      toast.error("There is already a view by that name.");
    } else {
      if (newName === "" || newName === undefined || newName === null) {
        setText(initialText);
        setInputValue(initialText);
        setIsEditing(false);
        toast.error("Empty input returned to original state.");
        return;
      }

      if (newName.length > 20) {
        setText(initialText);
        setInputValue(initialText);
        setIsEditing(false);
        toast.error("Input name can't be longer than 20 characters.");
        return;
      }
      setText(newName);
      updateInStoreAndDatabase(newName);
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleBlur(e as unknown as FocusEvent<HTMLInputElement>);
    }
  };

  function updateInStoreAndDatabase(newName: string) {
    if (updateStoreLocation == "pricesConfigs") {
      updateDisplayNamePriceConfigInStoreAndAPI({
        newName,
        config,
        getAccessTokenSilently,
      });
    }
  }

  return (
    <div
      data-testid={`EditableTextContainer-${config.id}`}
      id={`EditableTextContainer-${config.id}`}
      className="min-w-0 flex-1"
    >
      {isEditing ? (
        // LQC-2575 - colours are set here rather than inherited: the row this sits in
        // flips its text between black and white by active state, and the AG Grid
        // group-title-bar class this used to borrow supplies a background but no
        // colour, so the text could land white-on-white while being typed.
        <input
          data-testid={`EditableTextInput-${config.id}`}
          id={`EditableTextInput-${config.id}`}
          type="text"
          // Without this the default 20-char width widens the row and its sidebar.
          size={1}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className={clsx(
            EDITABLE_TEXT_BOX_CLASS,
            "border-sky-500 bg-white !text-black outline-none focus:border-sky-400 dark:bg-cubeBaseDark-700 dark:!text-white",
          )}
        />
      ) : (
        <span
          data-testid={`EditableTextValue-${config.id}`}
          id={`EditableTextValue-${config.id}`}
          className={clsx(
            EDITABLE_TEXT_BOX_CLASS,
            "flex items-center overflow-hidden whitespace-nowrap border-transparent",
          )}
          onDoubleClick={handleDoubleClick}
        >
          {text}
        </span>
      )}
    </div>
  );
};

export default EditableText;
